import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import StatsBar from '../ui/StatsBar';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDateTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const formatTimeAgo = (date) => {
  if (!date) return '-';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

const getAutotaskContactUrl = (autotaskId) => {
  return `https://ww29.autotask.net/Mvc/CRM/ContactDetail.mvc?ids%5B0%5D=${autotaskId}&ContactId=${autotaskId}`;
};

const getHubSpotContactUrl = (hubspotId) => {
  return `https://app.hubspot.com/contacts/23367316/contact/${hubspotId}`;
};

const getSyncStatusLabel = (status, message = '') => {
  // Check if error message indicates partial sync
  if (status === 'error' && message && message.toLowerCase().includes('partially synced')) {
    return 'Partially Synced';
  }
  const labels = {
    success: 'Success',
    synced: 'Synced',
    pending: 'Pending',
    processing: 'Processing',
    error: 'Error',
    partially_synced: 'Partially Synced'
  };
  return labels[status] || 'Unknown';
};

const getSyncStatusClass = (status, message = '') => {
  // Check if error message indicates partial sync
  if (status === 'error' && message && message.toLowerCase().includes('partially synced')) {
    return 'partially_synced';
  }
  return status || 'pending';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Contacts = ({ contacts, stats, loading, pagination, fetchContacts, apiFetch }) => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [syncHistory, setSyncHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const [filters, setFilters] = useState({
    syncStatus: 'all',
    hasCompany: 'all',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'last_sync_at',
    direction: 'desc'
  });

  // Pagination state - synced with server
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Server-side search with debounce
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  // Debounced search handler - triggers API call after 500ms of no typing
  const handleSearchChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, searchTerm: value }));

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setServerSearchTerm(value);
      setCurrentPage(1);
      fetchContacts(1, itemsPerPage, sortConfig.field, sortConfig.direction, value);
    }, 500);
  }, [fetchContacts, itemsPerPage, sortConfig]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // MODAL FUNCTIONS
  // ===========================================================================

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedContact(null);
    setSyncHistory(null);
    setRetryResult(null);
  };

  const loadSyncHistory = async (contactId) => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch(`/contacts/${contactId}/history`);
      const result = await response.json();
      if (result.success) {
        setSyncHistory(result.history);
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRetrySync = async (contactId) => {
    setLoadingRetry(true);
    setRetryResult(null);
    try {
      const response = await fetch('https://flow.ccassistant.app/webhook/9b29a51e-e6c3-4e80-85da-39722bb36e58', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contact_id: contactId }),
      });
      const result = await response.json();
      setRetryResult({ success: response.ok, data: result });
      // Refresh history after a short delay to show the new sync attempt
      setTimeout(() => {
        loadSyncHistory(contactId);
      }, 2000);
    } catch (error) {
      console.error('Error triggering retry:', error);
      setRetryResult({ success: false, error: error.message });
    } finally {
      setLoadingRetry(false);
    }
  };

  // ===========================================================================
  // FILTERING LOGIC
  // ===========================================================================

  const filteredContacts = useMemo(() => {
    // Search is now done server-side, only apply local filters
    return contacts.filter(contact => {
      // Sync status filter
      if (filters.syncStatus !== 'all') {
        const derivedStatus = getSyncStatusClass(contact.sync_status, contact.sync_message);
        if (filters.syncStatus === 'partially_synced') {
          // For partially_synced filter, check the derived status
          if (derivedStatus !== 'partially_synced') return false;
        } else if (filters.syncStatus === 'error') {
          // For error filter, exclude partially synced (which have error status but partial message)
          if (contact.sync_status !== 'error' || derivedStatus === 'partially_synced') return false;
        } else {
          if (contact.sync_status !== filters.syncStatus) return false;
        }
      }

      // Has company filter
      if (filters.hasCompany === 'yes' && !contact.company_name) {
        return false;
      }
      if (filters.hasCompany === 'no' && contact.company_name) {
        return false;
      }

      return true;
    });
  }, [contacts, filters]);

  // ===========================================================================
  // SORTING LOGIC
  // ===========================================================================

  // Fields that are sorted by the backend - don't re-sort these
  const backendSortedFields = ['created_at', 'last_sync_at'];

  const sortedContacts = useMemo(() => {
    // If sorting by a backend-handled field, don't re-sort - data is already sorted
    if (backendSortedFields.includes(sortConfig.field)) {
      return filteredContacts;
    }

    // For other fields (like 'name'), sort on frontend
    return [...filteredContacts].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      // Handle special fields
      if (sortConfig.field === 'name') {
        aValue = `${a['First Name'] || ''} ${a['Last Name'] || ''}`.trim();
        bValue = `${b['First Name'] || ''} ${b['Last Name'] || ''}`.trim();
      }

      // Handle null, undefined, and empty string values
      const aEmpty = aValue === null || aValue === undefined || aValue === '';
      const bEmpty = bValue === null || bValue === undefined || bValue === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      // Compare
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredContacts, sortConfig]);

  const handleSort = (field) => {
    const newDirection = sortConfig.field === field && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    setSortConfig({ field, direction: newDirection });
    setCurrentPage(1);
    // Fetch from backend with new sort parameters (include search)
    fetchContacts(1, itemsPerPage, field, newDirection, serverSearchTerm);
  };

  // ===========================================================================
  // PAGINATION LOGIC (Server-side)
  // ===========================================================================

  const totalPages = pagination?.total_pages || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchContacts(page, itemsPerPage, sortConfig.field, sortConfig.direction, serverSearchTerm);
  };

  const handleItemsPerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    fetchContacts(1, newPerPage, sortConfig.field, sortConfig.direction, serverSearchTerm);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const clearFilters = () => {
    setFilters({
      syncStatus: 'all',
      hasCompany: 'all',
      searchTerm: ''
    });
    // Also clear server-side search and refresh
    if (serverSearchTerm) {
      setServerSearchTerm('');
      setCurrentPage(1);
      fetchContacts(1, itemsPerPage, sortConfig.field, sortConfig.direction, '');
    }
  };

  // ===========================================================================
  // ENHANCED STATS FOR BOSS VIEW
  // ===========================================================================

  const enhancedStats = {
    ...stats,
    entityType: 'Contacts',
    customStats: [
      { label: 'With Company', value: stats.with_company, color: '#10B981' },
      { label: 'Fully Synced', value: stats.fully_synced, color: '#8B5CF6' },
      { label: 'Recently Active (30d)', value: stats.recently_active, color: '#059669' },
      { label: 'HubSpot Coverage', value: `${stats.hubspot_coverage}%`, color: stats.hubspot_coverage > 90 ? '#10B981' : '#F59E0B' },
      { label: 'Sync Coverage', value: `${stats.sync_coverage}%`, color: stats.sync_coverage > 90 ? '#10B981' : '#F59E0B' }
    ]
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <>
      {/* Stats Bar */}
      <StatsBar stats={enhancedStats} />

      {/* Filters Section */}
      <div className="filters-section">
        <input
          type="text"
          className="search-bar"
          placeholder="🔍 Search by name, email, or ID... (searches all pages)"
          value={filters.searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <div className="filters-row">
          <select
            className="filter-select"
            value={filters.syncStatus}
            onChange={(e) => setFilters({ ...filters, syncStatus: e.target.value })}
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="synced">Synced</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="partially_synced">Partially Synced</option>
            <option value="error">Error</option>
          </select>

          <select
            className="filter-select"
            value={filters.hasCompany}
            onChange={(e) => setFilters({ ...filters, hasCompany: e.target.value })}
          >
            <option value="all">All Contacts</option>
            <option value="yes">With Company</option>
            <option value="no">Without Company</option>
          </select>

          {(filters.searchTerm || filters.syncStatus !== 'all' || filters.hasCompany !== 'all') && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="table-header">
          <div className="table-title">
            {loading
              ? 'Loading...'
              : `Showing ${sortedContacts.length} of ${pagination?.total || stats.total} contacts (Page ${currentPage} of ${totalPages})`}
          </div>
          <div className="table-actions">
            <button className="icon-btn-small" onClick={() => fetchContacts(currentPage, itemsPerPage, sortConfig.field, sortConfig.direction, serverSearchTerm)}>
              🔄 Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            Loading contacts...
          </div>
        ) : (
          <table className="contacts-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  Name {sortConfig.field === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Email</th>
                <th>Company</th>
                <th className="sortable-header" onClick={() => handleSort('created_at')}>
                  Created {sortConfig.field === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Autotask ID</th>
                <th>HubSpot ID</th>
                <th className="sortable-header" onClick={() => handleSort('sync_status')}>
                  Sync Status {sortConfig.field === 'sync_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('last_sync_at')}>
                  Last Sync {sortConfig.field === 'last_sync_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedContacts.map((contact, index) => (
                <tr key={contact.id || index}>
                  <td title={`${contact['First Name'] || ''} ${contact['Last Name'] || ''}`}>
                    <strong>
                      {contact['First Name'] || ''} {contact['Last Name'] || '-'}
                    </strong>
                    {contact.is_fully_synced && (
                      <span style={{ marginLeft: 8, color: '#10B981', fontSize: 12 }}>✓</span>
                    )}
                  </td>
                  <td title={contact.Email}>
                    {contact.Email || <span style={{ color: '#9CA3AF' }}>-</span>}
                  </td>
                  <td title={contact.company_name}>
                    {contact.company_name || <span style={{ color: '#9CA3AF' }}>-</span>}
                  </td>
                  <td>{formatDate(contact.created_at)}</td>
                  <td>
                    {contact.Autotask_id || contact.autotask_id ? (
                      <a
                        href={getAutotaskContactUrl(contact.Autotask_id || contact.autotask_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {contact.Autotask_id || contact.autotask_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    {contact.hubspot_id ? (
                      <a
                        href={getHubSpotContactUrl(contact.hubspot_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {contact.hubspot_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${getSyncStatusClass(contact.sync_status, contact.sync_message)}`}>
                      {getSyncStatusLabel(contact.sync_status, contact.sync_message)}
                    </span>
                  </td>
                  <td>
                    {contact.last_sync_at ? (
                      <span title={formatDateTime(contact.last_sync_at)}>
                        {formatTimeAgo(contact.last_sync_at)}
                        {!contact.is_recently_active && (
                          <span className="stale-indicator" title="No activity in 30+ days"></span>
                        )}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => {
                        setSelectedContact(contact);
                        setShowDetailsModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && pagination && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages} ({pagination.total} total items)
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ marginLeft: 12, padding: '4px 8px', borderRadius: 6 }}
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.has_prev}
              >
                First
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
              >
                Prev
              </button>
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '10px 8px' }}>...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
              >
                Next
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={!pagination.has_next}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DETAILS MODAL */}
      {showDetailsModal && selectedContact && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedContact['First Name']} {selectedContact['Last Name']}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Contact Details Section */}
              <div className="detail-section">
                <h3>Contact Details</h3>
                <div className="detail-row">
                  <strong>Name:</strong> {selectedContact['First Name']} {selectedContact['Last Name']}
                </div>
                <div className="detail-row">
                  <strong>Email:</strong> {selectedContact.Email || '-'}
                </div>
                <div className="detail-row">
                  <strong>Company:</strong> {selectedContact.company_name || '-'}
                </div>
                <div className="detail-row">
                  <strong>Created:</strong> {formatDateTime(selectedContact.created_at)}
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  {selectedContact.is_fully_synced ? (
                    <span style={{ color: '#10B981', fontWeight: 600 }}>✓ Fully Synced</span>
                  ) : (
                    <span style={{ color: '#F59E0B', fontWeight: 600 }}>⚠ Partial Sync</span>
                  )}
                </div>
              </div>

              {/* System Links Section */}
              <div className="detail-section">
                <h3>System Links</h3>
                <div className="detail-row">
                  <strong>Autotask ID:</strong>{' '}
                  {selectedContact.Autotask_id || selectedContact.autotask_id ? (
                    <a
                      href={getAutotaskContactUrl(selectedContact.Autotask_id || selectedContact.autotask_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedContact.Autotask_id || selectedContact.autotask_id} 🔗
                    </a>
                  ) : (
                    'Not synced'
                  )}
                </div>
                <div className="detail-row">
                  <strong>HubSpot ID:</strong>{' '}
                  {selectedContact.hubspot_id ? (
                    <a
                      href={getHubSpotContactUrl(selectedContact.hubspot_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedContact.hubspot_id} 🔗
                    </a>
                  ) : (
                    'Not synced'
                  )}
                </div>
              </div>

              {/* Sync Status Section */}
              <div className="detail-section">
                <h3>Sync Status</h3>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${getSyncStatusClass(selectedContact.sync_status, selectedContact.sync_message)}`}>
                    {getSyncStatusLabel(selectedContact.sync_status, selectedContact.sync_message)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Last Synced:</strong> {formatDateTime(selectedContact.last_sync_at)}
                </div>
                {selectedContact.sync_message && (
                  <div className="detail-row">
                    <strong>Message:</strong>
                    <div className={
                      selectedContact.sync_status === 'error'
                        ? 'error-message-box'
                        : 'success-message-box'
                    }>
                      {selectedContact.sync_message}
                    </div>
                  </div>
                )}
              </div>

              {/* Sync History Section */}
              <div className="detail-section">
                <h3>Sync History</h3>
                {!syncHistory ? (
                  <button
                    className="btn-secondary"
                    onClick={() => loadSyncHistory(selectedContact.hubspot_id || selectedContact.autotask_id)}
                    disabled={loadingHistory}
                  >
                    {loadingHistory ? 'Loading...' : 'Load History'}
                  </button>
                ) : (
                  <div className="sync-history">
                    {syncHistory.length === 0 ? (
                      <div style={{padding: 12, textAlign: 'center', color: '#6B7280'}}>
                        No sync history available
                      </div>
                    ) : (
                      syncHistory.map((entry, idx) => (
                        <div key={idx} className="sync-log-entry">
                          <div>
                            <span className={`status-badge ${entry.sync_status}`}>
                              {entry.sync_status}
                            </span>
                            {entry.message && (
                              <div className="text-muted" style={{marginTop: 4, fontSize: 12}}>
                                {entry.message}
                              </div>
                            )}
                          </div>
                          <div className="sync-log-time">
                            <span className="text-muted" style={{fontSize: 12}}>
                              {formatDateTime(entry.created_at)}
                            </span>
                            {entry.execution_id && entry.workflow_id && (
                              <>
                                <span className="text-muted" style={{fontSize: 12}}> • </span>
                                <a
                                  href={`https://flow.ccassistant.app/workflow/${entry.workflow_id}/executions/${entry.execution_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="execution-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Execution
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Retry Sync Section */}
              <div className="detail-section">
                <h3>Actions</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Manually trigger a sync for this contact to Autotask.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => handleRetrySync(selectedContact.hubspot_id)}
                  disabled={loadingRetry}
                  style={{ width: '100%' }}
                >
                  <span>{loadingRetry ? '↻' : '↻'}</span>
                  {loadingRetry ? 'Syncing...' : 'Retry Sync'}
                </button>
                {retryResult && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 6,
                    background: retryResult.success ? '#d1fae5' : '#fee2e2',
                    color: retryResult.success ? '#065f46' : '#991b1b',
                    fontSize: 13
                  }}>
                    {retryResult.success
                      ? 'Sync triggered successfully!'
                      : `Error: ${retryResult.error}`
                    }
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Contacts;
